import React, { useState, useEffect, useContext } from 'react';
import { Container, Typography, Box, CircularProgress, Paper, Divider } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../axiosConfig';
import DOMPurify from 'dompurify';
import { ConfigLinksDetailsContext } from '../Context/cofingLinksDetailsContext';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const AR_MONTHS = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
const EN_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDate = (dateStr, isRTL) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const day = String(d.getDate()).padStart(2, '0');
    const month = (isRTL ? AR_MONTHS : EN_MONTHS)[d.getMonth()];
    return `${day} ${month} ${d.getFullYear()}`;
};

// Pick the right language field, fallback to the other if empty
const pick = (ar, en, isRTL) => {
    if (isRTL) return ar || en || '';
    return en || ar || '';
};

const NewsDetails = () => {
    const { id } = useParams();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { configLinks, isConfigLoaded } = useContext(ConfigLinksDetailsContext);
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const isRTL = i18n.language?.startsWith('ar');
    const newsCategoryId = Number(configLinks?.newsCatId);

    useEffect(() => {
        if (!isConfigLoaded || !id) return;

        if (!(newsCategoryId > 0)) {
            setLoading(false);
            navigate('/news');
            return;
        }

        let isCancelled = false;

        const fetchArticle = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/CmsArticle/${newsCategoryId}?articleId=${id}`);

                let foundArticle = null;
                if (Array.isArray(response) && response.length > 0) {
                    foundArticle = response[0];
                } else if (response?.dataList && response.dataList.length > 0) {
                    foundArticle = response.dataList[0];
                }

                if (foundArticle) {
                    if (!isCancelled) {
                        setArticle(foundArticle);
                    }
                } else {
                    navigate('/news');
                }
            } catch (error) {
                console.error('Error fetching article:', error);
                navigate('/news');
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        fetchArticle();

        return () => {
            isCancelled = true;
        };
    }, [id, isConfigLoaded, navigate, newsCategoryId]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    if (!article) return null;

    // Prefer bilingual fields if present, fall back to pre-resolved field
    const title   = pick(article.articleTitleAr, article.articleTitleEn, isRTL) || article.articleTitle || '';
    const rawText = pick(article.articleTextAr,  article.articleTextEn,  isRTL) || article.articleText  || '';
    // Remove the first <img> to avoid duplicating the cover image shown above
    const text    = article.coverImageUrl
        ? rawText.replace(/<img[^>]*\/?>/, '')
        : rawText;
    const dateStr = formatDate(article.publishDate, isRTL);

    return (
        <Container sx={{ py: 5, minHeight: '60vh', maxWidth: '800px !important' }} dir={isRTL ? 'rtl' : 'ltr'}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, border: '1px solid #eaecef' }}>
                <Typography variant='h4' fontWeight='bold' color='primary.main' mb={1}>
                    {title}
                </Typography>
                {dateStr && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                        <CalendarTodayIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                        <Typography variant='caption' sx={{ color: 'text.disabled', fontWeight: 500 }}>
                            {dateStr}
                        </Typography>
                    </Box>
                )}
                <Divider sx={{ mb: 4 }} />

                {article.coverImageUrl && (
                    <Box
                        component='img'
                        src={article.coverImageUrl}
                        alt={title}
                        sx={{ width: '100%', maxHeight: 400, objectFit: 'cover', borderRadius: 2, mb: 4 }}
                    />
                )}

                <Box
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="ck-content"
                    sx={{
                        '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2 },
                        lineHeight: 1.8,
                        fontSize: '1.1rem'
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
                />
            </Paper>
        </Container>
    );
};

export default NewsDetails;
